import React, { FC } from "react";
import {
  Alignment,
  BackgroundType,
  DismissPosition,
  DismissType,
  InAppPosition,
  InAppState,
  MediaPosition,
  PrimaryButtonPosition,
} from "pages/InAppBuilder/types";

interface InAppPreviewProps {
  state: InAppState;
}

// Render the modal positioned + styled. Static, lightweight — for picker preview only.
// Edge padding (`PAD`) is baked into anchored positions so they sit inside the
// phone frame instead of flush against it. CENTER intentionally has no
// padding so the translate-based centering isn't thrown off.
const PAD = 12;
const positionStyles = (pos: InAppPosition): React.CSSProperties => {
  switch (pos) {
    case InAppPosition.TOP_LEFT:
      return { top: PAD, left: PAD };
    case InAppPosition.TOP_CENTER:
      return { top: PAD, left: "50%", transform: "translateX(-50%)" };
    case InAppPosition.TOP_RIGHT:
      return { top: PAD, right: PAD };
    case InAppPosition.CENTER:
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    case InAppPosition.BOTTOM_LEFT:
      return { bottom: PAD, left: PAD };
    case InAppPosition.BOTTOM_CENTER:
      return { bottom: PAD, left: "50%", transform: "translateX(-50%)" };
    case InAppPosition.BOTTOM_RIGHT:
      return { bottom: PAD, right: PAD };
    default:
      return {};
  }
};

const buttonPositionStyles = (
  pos: PrimaryButtonPosition
): React.CSSProperties => {
  switch (pos) {
    case PrimaryButtonPosition.BOTTOM_LEFT:
      return { alignSelf: "flex-start" };
    case PrimaryButtonPosition.BOTTOM_RIGHT:
      return { alignSelf: "flex-end" };
    case PrimaryButtonPosition.CENTER_RIGHT:
      return { alignSelf: "flex-end" };
    case PrimaryButtonPosition.BOTTOM_CENTER:
    default:
      return { alignSelf: "center" };
  }
};

const renderBackground = (state: InAppState): string => {
  const bg = state.background;
  if (bg.selected === BackgroundType.SOLID) {
    return bg[BackgroundType.SOLID].color;
  }
  if (bg.selected === BackgroundType.GRADIENT) {
    const { color1, color2 } = bg[BackgroundType.GRADIENT];
    return `linear-gradient(135deg, ${color1}, ${color2})`;
  }
  if (bg.selected === BackgroundType.IMAGE) {
    const src = bg[BackgroundType.IMAGE].imageSrc;
    return src ? `url(${src}) center/cover` : "#e5e7eb";
  }
  return "#FFFFFF";
};

// Strip basic markdown for preview (the laudspeaker editor uses **bold** etc.)
const renderText = (raw: string) => {
  return raw
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br/>");
};

const PHONE_W = 360;
const PHONE_H = 560;

const InAppPreview: FC<InAppPreviewProps> = ({ state }) => {
  // Scale the configured width to fit a small preview phone frame.
  const configuredW = state.width.value;
  const scale = Math.min(1, (PHONE_W - 24) / configuredW);
  const modalWidth = configuredW * scale;
  const titleFont = state.title.fontSize * scale;
  const bodyFont = state.body.fontSize * scale;

  const showShroud = !state.shroud.hidden;
  const showTitle = !state.title.hidden;
  const showBody = !state.body.hidden;
  const showMedia = !state.media.hidden;
  const showButton = !state.primaryButton.hidden;
  const showDismiss = !state.dismiss.hidden;

  return (
    <div className="flex h-full w-full items-center justify-center bg-themed-background">
      <div
        className="relative overflow-hidden rounded-[28px] border border-themed-border shadow-md"
        style={{
          width: PHONE_W,
          height: PHONE_H,
          background: "#F3F4F6",
        }}
      >
        {/* Fake app chrome */}
        <div className="absolute top-0 left-0 right-0 h-7 flex items-center justify-center text-[10px] text-themed-textSecondary">
          App
        </div>

        {showShroud && (
          <div
            className="absolute inset-0"
            style={{
              background: state.shroud.color,
              opacity: state.shroud.opacity,
              backdropFilter: `blur(${state.shroud.blur}px)`,
            }}
          />
        )}

        <div
          className="absolute flex flex-col overflow-hidden"
          style={{
            ...positionStyles(state.position),
            width: modalWidth,
            borderRadius: state.borderRadius.value * scale,
            background: renderBackground(state),
            padding: 14 * scale,
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
          }}
        >
          {showDismiss && (
            <div
              style={{
                position: "absolute",
                top: 6,
                right: 8,
                color: state.dismiss.color,
                fontSize: 14 * scale,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {state.dismiss.type === DismissType.CROSS
                ? "×"
                : state.dismiss.content}
            </div>
          )}

          {showMedia && state.media.position === MediaPosition.TOP && (
            <div
              style={{
                height: 90 * scale,
                marginBottom: 10 * scale,
                background: "#E5E7EB",
                borderRadius: 8 * scale,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10 * scale,
                color: "#9CA3AF",
              }}
            >
              image
            </div>
          )}

          {showTitle && state.title.content.trim() && (
            <div
              style={{
                color: state.title.textColor,
                fontSize: titleFont,
                fontWeight: 700,
                lineHeight: 1.25,
                textAlign:
                  state.title.alignment === Alignment.CENTER
                    ? "center"
                    : state.title.alignment === Alignment.RIGHT
                    ? "right"
                    : "left",
                marginBottom: 6 * scale,
              }}
              dangerouslySetInnerHTML={{
                __html: renderText(state.title.content),
              }}
            />
          )}

          {showBody && (
            <div
              style={{
                color: state.body.textColor,
                fontSize: bodyFont,
                lineHeight: 1.45,
                textAlign:
                  state.body.alignment === Alignment.CENTER
                    ? "center"
                    : state.body.alignment === Alignment.RIGHT
                    ? "right"
                    : "left",
              }}
              dangerouslySetInnerHTML={{
                __html: renderText(state.body.content),
              }}
            />
          )}

          {showButton && state.primaryButton.content.trim() && (
            <div
              style={{
                marginTop: 12 * scale,
                ...buttonPositionStyles(state.primaryButton.position),
              }}
            >
              <button
                type="button"
                style={{
                  background: state.primaryButton.fillColor,
                  color: state.primaryButton.textColor,
                  border: `1px solid ${state.primaryButton.borderColor}`,
                  borderRadius: state.primaryButton.borderRadius.value * scale,
                  padding: `${7 * scale}px ${16 * scale}px`,
                  fontSize: 12 * scale,
                  fontWeight: 600,
                  cursor: "default",
                }}
              >
                {state.primaryButton.content}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InAppPreview;
